(() => {
  const DEBUG = new URLSearchParams(window.location.search).has('video_debug');
  const interactionEvents = ['pointerdown', 'touchend', 'click', 'keydown'];
  const instances = new WeakMap();

  const now = () => Math.round(performance.now());

  const snapshot = (video) => ({
    currentSrc: video.currentSrc,
    currentTime: Number(video.currentTime.toFixed(3)),
    readyState: video.readyState,
    networkState: video.networkState,
    paused: video.paused,
    ended: video.ended,
    muted: video.muted,
    defaultMuted: video.defaultMuted,
    autoplay: video.autoplay,
    playsInline: video.playsInline,
    controls: video.controls,
    loop: video.loop,
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    buffered: Array.from({ length: video.buffered.length }, (_, index) => [video.buffered.start(index), video.buffered.end(index)]),
    visibility: document.visibilityState,
    rect: (() => {
      const rect = video.getBoundingClientRect();
      return { width: Math.round(rect.width), height: Math.round(rect.height), top: Math.round(rect.top), left: Math.round(rect.left) };
    })(),
    userAgent: navigator.userAgent,
    connection: navigator.connection
      ? { effectiveType: navigator.connection.effectiveType, saveData: navigator.connection.saveData }
      : null,
    autoplayPolicy: typeof navigator.getAutoplayPolicy === 'function' ? navigator.getAutoplayPolicy(video) : undefined,
    error: video.error ? { code: video.error.code, message: video.error.message } : null,
  });

  const createDebugPanel = (video, state) => {
    if (!DEBUG || document.querySelector('[data-hero-video-debug]')) return null;
    const panel = document.createElement('aside');
    panel.dataset.heroVideoDebug = '';
    Object.assign(panel.style, {
      position: 'fixed', zIndex: '2147483647', inset: '12px auto auto 12px', maxWidth: 'min(92vw, 560px)',
      maxHeight: '45vh', overflow: 'auto', padding: '10px', background: '#111', color: '#fff',
      font: '12px/1.35 monospace', whiteSpace: 'pre-wrap', borderRadius: '6px',
    });
    const copy = document.createElement('button');
    copy.type = 'button';
    copy.textContent = 'Copy video logs';
    copy.style.cssText = 'margin-bottom:8px;padding:4px 6px;';
    const output = document.createElement('pre');
    output.style.margin = '0';
    panel.append(copy, output);
    document.body.append(panel);
    copy.addEventListener('click', () => navigator.clipboard?.writeText(state.logs.map((entry) => JSON.stringify(entry)).join('\n')));
    state.render = () => { output.textContent = state.logs.map((entry) => JSON.stringify(entry)).join('\n'); };
    return panel;
  };

  const init = (video) => {
    if (!(video instanceof HTMLVideoElement) || instances.has(video)) return;
    const shell = video.closest('[data-hero-video-shell]');
    if (!shell) return;
    const priority = video.dataset.heroVideoPriority === 'true';
    const state = {
      logs: [],
      playPromise: null,
      started: false,
      eligibleToPlay: priority,
      render: null,
      observer: null,
      controller: new AbortController(),
    };
    const { signal } = state.controller;
    instances.set(video, state);
    const debug = (type, details = {}) => {
      if (!DEBUG) return;
      state.logs.push({ t: now(), type, ...details, state: snapshot(video) });
      state.logs = state.logs.slice(-120);
      state.render?.();
    };
    createDebugPanel(video, state);
    const mediaEvents = ['loadstart', 'durationchange', 'loadedmetadata', 'loadeddata', 'progress', 'canplay', 'canplaythrough', 'play', 'playing', 'pause', 'waiting', 'stalled', 'suspend', 'abort', 'emptied', 'error', 'ended'];
    mediaEvents.forEach((type) => video.addEventListener(type, () => debug(type), { signal }));
    document.addEventListener('visibilitychange', () => {
      debug('visibilitychange');
      if (document.visibilityState === 'visible') start('visibilitychange');
    }, { signal });
    window.addEventListener('pageshow', () => {
      debug('pageshow');
      start('pageshow');
    }, { signal });
    window.addEventListener('pagehide', () => debug('pagehide'), { signal });

    const removeInteractionListeners = () => interactionEvents.forEach((type) => document.removeEventListener(type, onInteraction, true));
    const onInteraction = () => {
      debug('interaction');
      start('interaction');
    };
    const addInteractionListeners = () => interactionEvents.forEach((type) => document.addEventListener(type, onInteraction, { capture: true, passive: true, signal }));

    const markPlayingAfterProgress = () => {
      const initialTime = video.currentTime;
      window.setTimeout(() => {
        if (video.currentTime > initialTime || video.currentTime > 0) {
          state.started = true;
          shell.dataset.heroVideoState = 'playing';
          removeInteractionListeners();
          debug('playing-confirmed');
        }
      }, 120);
    };
    video.addEventListener('playing', markPlayingAfterProgress, { once: false, signal });

    function start(reason) {
      if (!state.eligibleToPlay) return null;
      if ((state.started && !video.paused && !video.ended) || state.playPromise || !video.isConnected) return state.playPromise;
      if (video.getClientRects().length === 0) return null;
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.playsInline = true;
      video.loop = true;
      video.controls = false;
      debug('play-request', { reason });
      state.playPromise = video.play();
      state.playPromise.then(() => debug('play-resolved')).catch((error) => {
        debug('play-rejected', { name: error?.name, message: error?.message, stack: error?.stack });
        addInteractionListeners();
      }).finally(() => { state.playPromise = null; });
      return state.playPromise;
    }

    video.addEventListener('loadedmetadata', () => {
      start('loadedmetadata');
    }, { signal });
    video.addEventListener('canplay', () => {
      start('canplay');
    }, { signal });

    if (!priority && 'IntersectionObserver' in window) {
      state.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== video) return;
          state.eligibleToPlay = entry.isIntersecting;
          debug('intersection', { isIntersecting: entry.isIntersecting, ratio: entry.intersectionRatio });
          if (entry.isIntersecting) start('intersection');
        });
      }, { rootMargin: '75% 0px 75% 0px', threshold: 0.01 });
      state.observer.observe(video);
    }

    addInteractionListeners();
    if (priority) start('initialization');
  };

  const initAll = (root = document) => root.querySelectorAll?.('[data-hero-video]')?.forEach(init);
  const boot = () => initAll();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-hero-video]')?.forEach((video) => {
      const state = instances.get(video);
      state?.observer?.disconnect();
      state?.controller.abort();
    });
  });
})();
